import { combineLatest, map } from 'rxjs';
import { perspectivesEditor$ } from './perspectivesEditor$/perspectivesEditor$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';
import { getAvailableGroupOptionsWorkspaces } from './utils';
import { getSidebarGroupingRuleNotificationParamsFunction } from '@ardoq/perspectives-sidebar';
import { type PerspectiveGroupingRule } from '@ardoq/perspectives';
import { Features, featureOperations } from '@ardoq/features';
import { featureContext$ } from 'streams/featureContext$';
import { getInvalidGroupingRuleSelectOptionsByRuleId } from './getInvalidGroupingRuleSelectOptionsByRuleId';
import { getGroupingRuleLabelsByRuleId } from 'streams/grouping/getGroupingRuleLabelsByRuleId';
import { groupingRuleLabelData$ } from 'streams/grouping/groupingRuleLabelData$';

import { dispatchAction } from '@ardoq/rxbeach';
import {
  reorderGroupingRules,
  updateGroupingRule,
  deleteGroupingRule,
  createGroupingRule,
} from './perspectivesEditor$/actions';
import {
  getRestrictedGroupFormattingWarningProps,
  isCreatingGroupingRuleDisabled,
  createGetGroupDisabledProps,
} from './groupUtils';

const onGroupingRuleReordered = (
  reorderedGroupingRules: PerspectiveGroupingRule[]
) => dispatchAction(reorderGroupingRules(reorderedGroupingRules));

const onGroupingRuleDeleted = (deletedGroupingRuleId: string) =>
  dispatchAction(deleteGroupingRule(deletedGroupingRuleId));

const onGroupingRuleUpdated = (
  updatedGroupingRuleId: string,
  updatedGroupingRule: Partial<PerspectiveGroupingRule>
) =>
  dispatchAction(
    updateGroupingRule({ updatedGroupingRuleId, updatedGroupingRule })
  );

const onGroupingRuleAdded = (newGroupingRule: PerspectiveGroupingRule) =>
  dispatchAction(createGroupingRule(newGroupingRule));

const groupsTab$ = combineLatest([
  perspectivesEditor$,
  i18nLocale$,
  featureContext$,
  groupingRuleLabelData$,
]).pipe(
  map(
    ([
      perspectivesEditorState,
      i18nLocale,
      featureContext,
      groupingRuleLabelData,
    ]) => {
      const {
        filtersTabPlainDataParams: { workspaceFilterIds },
        groupsTab: { groupOptions, groupingRules, rulesIdsWithWarnings },
        activeViewId,
      } = perspectivesEditorState;

      const groupingOptions = {
        ...groupOptions,
        workspaces: getAvailableGroupOptionsWorkspaces(
          groupOptions.workspaces,
          workspaceFilterIds
        ),
      };

      const getRuleNotificationParams = rulesIdsWithWarnings.length
        ? getSidebarGroupingRuleNotificationParamsFunction(rulesIdsWithWarnings)
        : undefined;

      const groupingRuleLabelsByRuleId = getGroupingRuleLabelsByRuleId(
        groupingRules,
        groupingRuleLabelData
      );

      const invalidGroupingSelectOptionsDataByRuleId =
        getInvalidGroupingRuleSelectOptionsByRuleId(
          groupingRules,
          groupOptions,
          groupingRuleLabelsByRuleId
        );

      const getInvalidGroupingSelectOptions = (rule: PerspectiveGroupingRule) =>
        invalidGroupingSelectOptionsDataByRuleId[rule.id] || {};

      const description = t(
        i18nLocale,
        'perspectiveSidebar:groupsTab.description',
        'Show the hierarchy and relationship between components.'
      );
      const tabDescription = {
        description,
      };

      const restrictedGroupFormattingWarningProps =
        getRestrictedGroupFormattingWarningProps(activeViewId, groupingRules);
      const disableAddPerspectiveGroupButton =
        isCreatingGroupingRuleDisabled(activeViewId);
      const getGroupDisabledProps = createGetGroupDisabledProps(activeViewId);

      return {
        groupingRules,
        onGroupingRuleAdded,
        onGroupingRuleDeleted,
        onGroupingRuleReordered,
        onGroupingRuleUpdated,
        groupingOptions,
        getRuleNotificationParams,
        getInvalidGroupingSelectOptions,
        tabDescription,
        restrictedGroupFormattingWarningProps,
        disableAddPerspectiveGroupButton,
        getGroupDisabledProps,
        isGroupingByTypeNamesEnabled: featureOperations.hasFeature(
          featureContext,
          Features.GROUPING_BY_TYPE_NAMES
        ),
        i18nLocale,
      };
    }
  )
);
export { groupsTab$ };
